import { useState, useEffect } from "react";
import { Search, Plus, FileText, Phone, Users, AlertCircle, MoreHorizontal } from "lucide-react";
import { listFarmNotes, addFarmNote, type FarmNote } from "../../../api/admin";
import { IconButton } from "../../primitives/IconButton";
import { cn } from "../../utils/cn";
import { Modal } from "../../primitives/Modal";
import { Textarea } from "../../primitives/Textarea";
import { Button } from "../../primitives/Button";

type ClientNotesListProps = {
  farmId: string;
  onNoteCreated?: () => void;
};

const NOTE_TYPES: { value: string; label: string; icon: React.ReactNode }[] = [
  { value: "general", label: "Notes", icon: <FileText size={14} strokeWidth={1.5} /> },
  { value: "call", label: "Appel", icon: <Phone size={14} strokeWidth={1.5} /> },
  { value: "meeting", label: "Rencontre", icon: <Users size={14} strokeWidth={1.5} /> },
  { value: "complaint", label: "Plainte", icon: <AlertCircle size={14} strokeWidth={1.5} /> },
  { value: "other", label: "Autre", icon: <MoreHorizontal size={14} strokeWidth={1.5} /> },
];

export function ClientNotesList({ farmId, onNoteCreated }: ClientNotesListProps) {
  const [notes, setNotes] = useState<FarmNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState<string | "all">("all");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [noteType, setNoteType] = useState("general");
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchNotes = async () => {
    if (!farmId) return;
    setLoading(true);
    try {
      const data = await listFarmNotes(farmId, {
        type: selectedType !== "all" ? selectedType : undefined,
        page_size: 100,
      });
      setNotes(data.items);
    } catch (e) {
      console.error("Failed to load notes:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes();
  }, [farmId, selectedType]);

  const handleCreate = async () => {
    if (!content.trim()) {
      setError("Le contenu de la note est requis");
      return;
    }
    setSaving(true);
    setError(null);
    const apiNoteType = noteType === "other" ? "internal" : noteType;
    try {
      await addFarmNote(farmId, { note_type: apiNoteType, content: content.trim() });
      await fetchNotes();
      setContent("");
      setNoteType("general");
      setCreateModalOpen(false);
      onNoteCreated?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur lors de l'ajout");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat("fr-CA", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(dateString));
  };

  const getNoteTypeInfo = (type: string) =>
    NOTE_TYPES.find((t) => t.value === type) || NOTE_TYPES[0];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <IconButton
          icon={<Plus size={16} strokeWidth={1.5} />}
          ariaLabel="Ajouter une note"
          intent="primary"
          size="sm"
          onClick={() => {
            setNoteType("general");
            setContent("");
            setError(null);
            setCreateModalOpen(true);
          }}
        />
      </div>

      <div className="flex gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setSelectedType("all")}
          className={cn(
            "px-3 py-1.5 rounded-md text-xs font-medium transition",
            selectedType === "all"
              ? "bg-primary text-primary-foreground"
              : "bg-surface-muted text-text hover:bg-surface"
          )}
        >
          Tous
        </button>
        {NOTE_TYPES.map((type) => (
          <button
            key={type.value}
            type="button"
            onClick={() => setSelectedType(type.value)}
            className={cn(
              "px-3 py-1.5 rounded-md text-xs font-medium transition flex items-center gap-1.5",
              selectedType === type.value
                ? "bg-primary text-primary-foreground"
                : "bg-surface-muted text-text hover:bg-surface"
            )}
          >
            {type.icon}
            {type.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-muted text-center py-8">Chargement...</div>
      ) : notes.length === 0 ? (
        <div className="text-sm text-muted text-center py-8">
          {selectedType !== "all"
            ? "Aucune note ne correspond au filtre"
            : "Aucune note pour le moment"}
        </div>
      ) : (
        <div className="space-y-3">
          {notes.map((note) => {
            const typeInfo = getNoteTypeInfo(note.note_type);
            return (
              <div
                key={note.id}
                className="rounded-md border border-border bg-surface p-4 shadow-sm"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-muted">{typeInfo.icon}</span>
                      <span className="text-xs font-medium text-text">{typeInfo.label}</span>
                      <span className="text-xs text-muted">•</span>
                      <span className="text-xs text-muted">
                        Créé le {formatDate(note.created_at)}
                      </span>
                    </div>
                    <div className="text-sm text-text whitespace-pre-wrap">{note.content}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        title="Ajouter une note"
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        showDefaultButtons={false}
        showCloseButton
      >
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-text mb-1">Type</label>
            <div className="flex flex-wrap gap-2">
              {NOTE_TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setNoteType(t.value)}
                  className={cn(
                    "px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5",
                    noteType === t.value
                      ? "bg-primary text-primary-foreground"
                      : "bg-surface-muted text-text hover:bg-surface"
                  )}
                >
                  {t.icon}
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <Textarea
            label="Contenu"
            placeholder="Contenu de la note..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={4}
          />
          {error && <p className="text-sm text-danger">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              label="Annuler"
              intent="default"
              size="sm"
              onClick={() => setCreateModalOpen(false)}
            />
            <Button
              label="Enregistrer"
              intent="primary"
              size="sm"
              onClick={handleCreate}
              disabled={saving || !content.trim()}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
